import { Button, DatePicker, Input, Select, Upload } from "antd";
import { TableProps } from "antd/es/table";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { useAppSelector } from "../../../app/store";
import DateIcon from "../../../assets/icons/DateIcon";
import DownloadIcon from "../../../assets/icons/DownloadIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import ReportRequestModal from "../../../components/Report/ReportRequestModal";
import CustomTable from "../../../components/ui/CustomTable";
import Loader from "../../../components/ui/Loader";
import Empty from "../../../components/Visit/EmptyVisit";
import {
  dateFormat,
  dbDateFormat,
  visitSearchTypeList,
} from "../../../constants/constants";
import { useGetDepartmentsQuery } from "../../../features/department/departmentApi";
import {
  useGetBusinessDataQuery,
  useGetEmployeesQuery,
} from "../../../features/employee/employeeApi";
import { useGetVisitReportMutation } from "../../../features/report/reportApi";
import {
  useBulkAssignVisitMutation,
  useGetTeamVisitListQuery,
  useLazyGetVisitListExportQuery,
} from "../../../features/visit/visitApi";
import useDebounce from "../../../hooks/useDebounce";
import useError from "../../../hooks/useError";
import useExcelDownload from "../../../hooks/useExcelDownload";
import useModal from "../../../hooks/useModal";
import { useQueryParams } from "../../../hooks/useQueryParams";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import {
  convertMinutesToHoursWithText,
  formatDate,
  formatTime,
} from "../../../lib/time";
import { toast } from "../../../lib/toast";
import { BulkAssignVisitRow, TeamVisitQueryParams } from "../../../types/visit";
import { countParPageSerialNumber } from "../../../utils/number";
import { capitalize } from "../../../utils/string";
import {
  selectArrayOption,
  selectSearchOption,
  selectStringOption,
  skipData,
  underscoreToCapitalizeString,
} from "../../../utils/utils";
import { VisitStatus, visitTabList } from "./MyVisit";

const { RangePicker } = DatePicker;

enum Status {
  created = "status-warning",
  completed = "status-success",
  reached = "status-info",
  started = "status-info",
  cancelled = "status-danger",
  rescheduled = "status-dark",
}

interface DataType {
  visitDate: string;
  visitTitle: string;
  note: string;
  status: keyof typeof Status;
  _id: string;
}

type UploadRow = Record<string, unknown>;

export type TeamVisitFilterType = Omit<
  TeamVisitQueryParams,
  "limit" | "skip" | "search_keyword"
>;

const searchTypeSelect = visitSearchTypeList?.map((item) => ({
  label: underscoreToCapitalizeString(item),
  value: item,
}));

export default function TeamVisit() {
  const navigate = useNavigate();
  const location = useLocation();
  const { query, setQuery } = useQueryParams();
  const { keyword, inputValue, handleInputChange } = useDebounce(
    500,
    query.search_keyword || "",
  );
  const { open, handleClose, handleOpen } = useModal<
    undefined,
    "REPORT_REQUEST"
  >();
  const { page, limit, handlePagination, handleLimit, handleFilter, filter } =
    useTable<TeamVisitFilterType>({
      defaultFilter: {
        search_type:
          (query.search_type as TeamVisitFilterType["search_type"]) ||
          undefined,
        department_id: query.department_id
          ? Number(query.department_id)
          : undefined,
        start_date: query.start_date || undefined,
        end_date: query.end_date || undefined,
        status: (query.status as TeamVisitFilterType["status"]) || undefined,
      },
    });

  useEffect(() => {
    const params = new URLSearchParams();
    const {
      limit: _limit,
      skip: _skip,
      ...filterWithoutPagination
    } = filter as
      (TeamVisitFilterType & { limit?: number; skip?: number }) | undefined;

    Object.entries(filterWithoutPagination || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.set(key, String(value));
      }
    });

    if (keyword) {
      params.set("search_keyword", keyword);
    }

    const nextQuery = params.toString();
    const currentQuery = location.search.replace("?", "");

    if (nextQuery !== currentQuery) {
      setQuery(params, { replace: true });
    }
  }, [filter, keyword, location.search, setQuery]);

  const { authInfo } = useAppSelector((state) => state.auth);
  const [bulkErrors, setBulkErrors] = useState<any[]>([]);
  const {
    data: teamVisitListData,
    isLoading: isTeamVisitDataLoading,
    isError,
    error,
    refetch: refetchTeamVisits,
  } = useGetTeamVisitListQuery({
    params: {
      ...filter,
      search_keyword: keyword,
      limit,
      skip: skipData(page, limit),
    },
  });
  const {
    data: businessData,
    isError: isBusinessError,
    error: businessError,
    isLoading: isBusinessLoading,
  } = useGetBusinessDataQuery(authInfo?.business_id?.toString() || "", {
    skip: !authInfo?.business_id,
  });
  const { data: employeesData } = useGetEmployeesQuery({
    params: { limit: 10, skip: 0, status: "active" },
  });
  const [
    getVisitReport,
    {
      isLoading: isGenerateLoading,
      isError: isGenerateError,
      error: generateError,
      isSuccess: isGenerateSuccess,
    },
  ] = useGetVisitReportMutation();
  const [
    bulkAssignVisit,
    {
      isLoading: isBulkAssignLoading,
      isError: isBulkAssignError,
      error: bulkAssignError,
      isSuccess: isBulkAssignSuccess,
      data: bulkAssignData,
    },
  ] = useBulkAssignVisitMutation();
  const [getVisitListExport, { isLoading: isExportLoading }] =
    useLazyGetVisitListExportQuery();
  const { downloadExcel: downloadTemplate } = useExcelDownload(
    "bulk-visit-assignment-template",
    true,
  );
  const { downloadExcel: downloadErrorReport } = useExcelDownload(
    "bulk-visit-assignment-errors",
    true,
  );
  const { downloadExcel: downloadVisitExport } = useExcelDownload(
    "visit-overview",
    false,
  );
  const {
    isError: isDepartmentError,
    error: departmentError,
    data: departmentData,
  } = useGetDepartmentsQuery({
    params: { skip: 0, limit: 0, is_published: 1 },
  });
  useError(isGenerateError, generateError, "Generate request report fail");
  useError(isError, error, "Get team visit data fail");
  useError(isBusinessError, businessError, "Get business data fail");
  useError(isDepartmentError, departmentError, "Get department data fail");
  useError(isBulkAssignError, bulkAssignError, "Bulk visit assignment fail");

  const formatDateTime = (val?: string | null) =>
    val ? `${formatTime(val)} - ${formatDate(val)}` : "-";

  const formatTimeSpent = (minutes?: number | null) =>
    minutes === null || minutes === undefined
      ? "-"
      : convertMinutesToHoursWithText(minutes);

  const getStatusLabel = (status?: string) => {
    if (status === "created") return "Assigned";
    if (status === "completed") return "Ended";
    return status ? capitalize(status) : "n/a";
  };

  const handleTemplateDownload = () => {
    const employees = employeesData?.data?.employees || [];
    const templateRows = employees
      .filter((employee) => employee.member?.profile?.email)
      .slice(0, 10)
      .map((employee, index) => ({
        "Employee Email": employee.member.profile.email,
        "Visit Date": dayjs()
          .add(index % 3, "day")
          .format(dbDateFormat),
        "Visit Title": `Client Visit ${index + 1}`,
        "Description (Optional)": `Demo visit for ${employee.member.profile.name || "employee"}`,
        "Image URL (Optional)": "",
      }));

    downloadTemplate(
      templateRows.length
        ? templateRows
        : [
            {
              "Employee Email": "active.employee@company.com",
              "Visit Date": dayjs().format(dbDateFormat),
              "Visit Title": "Client Meeting",
              "Description (Optional)":
                "Replace email with an active employee email",
              "Image URL (Optional)": "",
            },
          ],
    );
  };

  const getUploadValue = (row: UploadRow, keys: string[]) => {
    const key = keys.find(
      (item) => row[item] !== undefined && row[item] !== null,
    );
    return key ? row[key] : "";
  };

  const toUploadText = (value: unknown) => {
    if (value === undefined || value === null) return "";
    return String(value).trim();
  };

  const toUploadDate = (value: unknown) => {
    if (value instanceof Date) return dayjs(value).format(dbDateFormat);

    if (typeof value === "number") {
      const parsedDate = XLSX.SSF.parse_date_code(value);
      if (parsedDate) {
        return dayjs(
          new Date(parsedDate.y, parsedDate.m - 1, parsedDate.d),
        ).format(dbDateFormat);
      }
    }

    return toUploadText(value);
  };

  const normalizeUploadRows = (rows: UploadRow[]): BulkAssignVisitRow[] =>
    rows.map((row) => ({
      employee_email: toUploadText(
        getUploadValue(row, ["Employee Email", "employee_email"]),
      ),
      visit_date: toUploadDate(
        getUploadValue(row, ["Visit Date", "visit_date"]),
      ),
      visit_title: toUploadText(
        getUploadValue(row, ["Visit Title", "visit_title"]),
      ),
      description: toUploadText(
        getUploadValue(row, ["Description (Optional)", "description"]),
      ),
      image_url: toUploadText(
        getUploadValue(row, ["Image URL (Optional)", "image_url"]),
      ),
    }));

  const handleBulkUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const workbook = XLSX.read(event.target?.result, {
        type: "array",
        cellDates: true,
      });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      bulkAssignVisit({ rows: normalizeUploadRows(rows) });
    };
    reader.readAsArrayBuffer(file);
    return false;
  };

  const handleVisitExport = async () => {
    const result = await getVisitListExport({
      params: {
        ...filter,
        search_keyword: keyword,
        skip: 0,
        limit: 0,
        manager_id: authInfo?.id,
      },
    }).unwrap();

    downloadVisitExport(
      (result?.data?.visits || []).map((visit) => ({
        "Visit Date": visit.visit_date ? formatDate(visit.visit_date) : "-",
        "Employee Name": visit.employee_name || "-",
        "Employee Email": visit.employee_email || "-",
        "Visit Title": visit.title || "-",
        "Assigned By": visit.assigned_by || "-",
        "Assigned Time": formatDateTime(visit.assigned_time),
        "Visit Status": getStatusLabel(visit.status),
        "Reached Time": formatDateTime(visit.reached_time),
        "End Time": formatDateTime(visit.end_time),
        "Time Spent": formatTimeSpent(visit.time_spent_in_minutes),
        Description: visit.description || "-",
      })),
    );
  };

  useEffect(() => {
    if (isBulkAssignSuccess && bulkAssignData?.data) {
      setBulkErrors(bulkAssignData.data.errors || []);
      const { total_rows, successful_assignments, failed_assignments } =
        bulkAssignData.data;
      const firstError = bulkAssignData.data.errors?.[0];
      const toastType =
        successful_assignments === 0 && failed_assignments > 0
          ? "error"
          : failed_assignments > 0
            ? "warning"
            : "success";

      toast(
        toastType,
        `Processed ${total_rows} rows. Success: ${successful_assignments}, Failed: ${failed_assignments}${
          firstError
            ? `. Row ${firstError.row_number}: ${firstError.error}`
            : failed_assignments
              ? ". Download Error Report for details."
              : ""
        }`,
      );
      refetchTeamVisits();
    }
  }, [bulkAssignData, isBulkAssignSuccess, refetchTeamVisits]);

  const columns: TableProps<DataType>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: <div className="text-nowrap w-[165px]">Visit Date</div>,
      dataIndex: "schedule_date",
      render: (val) => (val ? formatDate(val) : "n/a"),
      // render: (val) => (val ? formatDateTime(val) : "n/a"),
    },
    {
      title: <div className="text-nowrap">Employee ID</div>,
      dataIndex: "visitor",
      render: (val) => val?.employee_id || "n/a",
    },
    {
      title: <div className="text-nowrap">Employee Name</div>,
      dataIndex: "visitor",
      render: (val) => val?.name || "n/a",
    },
    {
      title: <div className="text-nowrap">Employee Email</div>,
      dataIndex: "visitor",
      render: (val) => val?.email || "n/a",
    },
    {
      title: <div className="text-nowrap">Visit Title</div>,
      dataIndex: "title",
      render: (val) => val || "n/a",
    },
    {
      title: <div className="text-nowrap">Description</div>,
      width: 300,
      dataIndex: "description",
      render: (val) => val || "n/a",
    },
    {
      title: "Status",
      dataIndex: "status",
      align: "center",
      render: (val: DataType["status"]) => (
        <span className={`${VisitStatus[val]}`}>{getStatusLabel(val)}</span>
      ),
    },
    {
      title: <div className="text-nowrap">Assigned By</div>,
      dataIndex: "assigned_by",
      render: (val) => val || "n/a",
    },
    {
      title: <div className="text-nowrap">Assigned Time</div>,
      dataIndex: "assigned_time",
      render: formatDateTime,
    },
    {
      title: <div className="text-nowrap">Reached Time</div>,
      dataIndex: "reached_time",
      render: formatDateTime,
    },
    {
      title: <div className="text-nowrap">End Time</div>,
      dataIndex: "end_time",
      render: formatDateTime,
    },
    {
      title: <div className="text-nowrap">Time Spent</div>,
      dataIndex: "time_spent_in_minutes",
      render: formatTimeSpent,
    },
    {
      title: "Action",
      dataIndex: "id",
      render: (val) => (
        <>
          <Link to={`${location.pathname}/view/${val}${location.search}`}>
            <Button
              className="action-button"
              size="small"
              icon={<ViewIcon />}
            />
          </Link>
        </>
      ),
      align: "center",
    },
  ];

  return (
    <SidebarLayout title="Visit Dashboard" tabList={visitTabList}>
      {isBusinessLoading ? (
        <Loader />
      ) : businessData?.data?.business?.is_enable_employee_visit ? (
        <CustomTable
          loading={isTeamVisitDataLoading}
          limit={limit}
          page={page}
          columns={columns}
          total={teamVisitListData?.data.total_visits}
          dataSource={teamVisitListData?.data.visits}
          scroll={{ x: 1500 }}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          filterSection={
            <>
              <Select
                showSearch
                placeholder="Search Type"
                value={filter?.search_type}
                options={searchTypeSelect}
                onChange={(val) => handleFilter("search_type", val)}
                filterOption={selectSearchOption}
                className="min-w-[120px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
              />
              <Input
                suffix={<SearchIcon />}
                placeholder={`Search by ${underscoreToCapitalizeString(
                  filter?.search_type || "name",
                  true,
                )}`}
                style={{ maxWidth: 180 }}
                value={inputValue}
                onChange={(val) => {
                  handleInputChange(val);
                }}
              />
              <Select
                allowClear
                showSearch
                placeholder="Department"
                filterOption={selectSearchOption}
                options={selectArrayOption(
                  departmentData?.data?.departments || [],
                  "name",
                  "id",
                )}
                className="min-w-[130px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
                value={filter?.department_id}
                onChange={(val) => {
                  handleFilter("department_id", val);
                }}
              />

              <RangePicker
                value={[
                  filter?.start_date ? dayjs(filter?.start_date) : null,
                  filter?.end_date ? dayjs(filter?.end_date) : null,
                ]}
                onChange={(e) => {
                  handleFilter("start_date", e?.[0]?.format(dbDateFormat));
                  handleFilter("end_date", e?.[1]?.format(dbDateFormat));
                }}
                format={dateFormat}
                style={{ width: 260 }}
                suffixIcon={<DateIcon />}
              />
              <Select
                allowClear
                showSearch
                placeholder="Status"
                filterOption={selectSearchOption}
                options={selectStringOption(
                  Object.keys(Status),
                  "string",
                  true,
                )}
                className="min-w-[120px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
                value={filter?.status}
                onChange={(e) => handleFilter("status", e)}
              />

              <Button
                onClick={handleTemplateDownload}
                className="download-button"
                icon={<DownloadIcon />}
              >
                Sample Template
              </Button>
              <Upload
                accept=".xlsx,.xls,.csv"
                showUploadList={false}
                beforeUpload={handleBulkUpload}
              >
                <Button
                  loading={isBulkAssignLoading}
                  className="download-button"
                  icon={<DownloadIcon />}
                >
                  Upload Excel
                </Button>
              </Upload>
              {bulkErrors.length ? (
                <Button onClick={() => downloadErrorReport(bulkErrors)}>
                  Error Report
                </Button>
              ) : null}
              <Button
                onClick={handleVisitExport}
                loading={isExportLoading}
                className="download-button"
                icon={<DownloadIcon />}
              >
                Export
              </Button>
              <Button
                onClick={() => handleOpen({ type: "REPORT_REQUEST" })}
                className="download-button"
                icon={<DownloadIcon />}
              >
                Report Requests
              </Button>
            </>
          }
        />
      ) : (
        <Empty
          setShowTable={() => navigate("/admin/settings/visit-settings")}
        />
      )}

      {open.type === "REPORT_REQUEST" && (
        <ReportRequestModal
          open={open.open}
          handleClose={handleClose}
          isLoading={isGenerateLoading}
          isSuccess={isGenerateSuccess}
          handleSubmit={(params: any) => {
            getVisitReport({
              body: {
                name: "Team visit report",
              },
              params: {
                ...filter,
                skip: 0,
                limit: 0,
                manager_id: authInfo?.id,
                ...params,
              },
            });
          }}
        />
      )}
    </SidebarLayout>
  );
}
